import { useEffect } from 'react';
import { useDisclosure, useMultiStyleConfig } from '@chakra-ui/react';
import Box from '../../Box/Box';
import Icon from '../../Icon/Icon';
import IconButton from '../../IconButton/IconButton';
import Popover from '../../Popover/Popover';
import PopoverContent from '../../Popover/PopoverContent';
import PopoverTrigger from '../../Popover/PopoverTrigger';
import Text from '../../Text/Text';
import Tooltip from '../../Tooltip/Tooltip';
import { FilterStyle } from '../Filter.theme';
import { FilterValue } from '../Filter.types';
import FilterForm from '../FilterForm/FilterForm';
import { useFilterContext } from '../Filter.context';
import { getOptionLabel } from '../Filter.utils';

export type FilterItemProps = {
  category: string;
};

const FilterItem = (props: FilterItemProps) => {
  const { category } = props;

  const { data, isLoading, onFilterChange, onFilterClear, setPopoverOpen, state } = useFilterContext();

  const { categoryName, categoryNamePlural, optionsMap, type, unfilteredLabel } = data[category];

  const value = state[category];

  const pluralCategoryString = (categoryNamePlural || `${category}s`).toLowerCase();

  const { isOpen, onClose: closePopover, onToggle: togglePopover } = useDisclosure();

  const filterStyle = useMultiStyleConfig('Filter') as FilterStyle;

  const onToggle = () => {
    togglePopover();
  };

  const onClose = () => {
    closePopover();
    setPopoverOpen(false);
  };

  const onChange = (newCategory: string, newValue: FilterValue) => {
    onClose();
    onFilterChange(newCategory, newValue);
  };

  const getText = () => {
    if (!value || value.length === 0) {
      return unfilteredLabel || `All ${pluralCategoryString}`;
    }
    if (value.length > 1) {
      return `${value.length} ${pluralCategoryString}`;
    }
    return `${categoryName || category}: ${getOptionLabel(value[0], optionsMap)}`;
  };

  useEffect(() => {
    if (isOpen) {
      setPopoverOpen(true);
    }
  }, [isOpen]);

  return (
    <Popover isLazy isOpen={isOpen} onClose={onClose}>
      <PopoverTrigger>
        <Box position={isOpen ? 'relative' : undefined} sx={filterStyle.item} zIndex={isOpen ? 'dialog' : undefined}>
          <Tooltip isDisabled={isLoading || type !== 'tag'} label="Edit">
            <Text
              as="button"
              data-testid={`${category}-trigger`}
              disabled={isLoading}
              onClick={onToggle}
              size="2"
              sx={filterStyle.tagEdit}
              type="button"
            >
              <Text size="2" maxWidth="320px" hasEllipsis>
                {getText()}
              </Text>
              {type === 'select' && <Icon name="ChevronDown" size="16" />}
            </Text>
          </Tooltip>
          {type !== 'select' && (
            <IconButton
              aria-label={isLoading ? '' : 'Clear'}
              iconName="Cross"
              isDisabled={isLoading}
              onClick={() => onFilterClear(category)}
              size="sm"
              sx={filterStyle.tagClear}
              tooltipProps={{ shouldWrapChildren: false }}
              variant="tertiary"
            />
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent>
        <FilterForm category={category} categoryName={categoryName} onCancel={onClose} onChange={onChange} />
      </PopoverContent>
    </Popover>
  );
};

export default FilterItem;
